import React from 'react';
import { Box } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { remCalc } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import useReports from 'state/hooks/use-reports';
import useClassrooms from 'state/hooks/use-classrooms';
import { UseReportsProps, ReportFilterTagProps } from 'state/providers/ReportsProvider/types';
import { Classroom } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import ReportFilterTag from './ReportFilterTag';
import { getSelectedGroups, getSelectedStudents } from '../../utils/reports-util';

type Props = {
  printOnly?: boolean;
};

const ReportFilterTags: React.FC<Props> = (props) => {
  const [reportState] = useReports();
  const [{ activeClassroom }] = useClassrooms();

  if (!activeClassroom) return null;

  if (activeClassroom?.students?.length === reportState.filteredStudents.length && !props.printOnly)
    return null;

  const filterTags = getFilterTags(reportState, activeClassroom);

  return (
    <Box mt={-3} className={props.printOnly ? 'printOnly' : 'filterTag'}>
      {filterTags && (
        <Box display="flex" flexWrap="wrap" gap={remCalc(12)}>
          {filterTags.map(({ label, icon }, index) => (
            <ReportFilterTag
              key={index}
              label={label}
              icon={icon && <FontAwesomeIcon icon={icon} />}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const getFilterTags = (
  reportState: UseReportsProps,
  classroom: Classroom
): ReportFilterTagProps[] => {
  const filters = reportState?.filters;
  return [
    ...getGroupsFilterTags(classroom, filters.studentIds),
    ...getStudentsFilterTags(classroom, filters.studentIds),
  ];
};

const getGroupsFilterTags = (
  classroom: Classroom,
  studentIds: number[]
): ReportFilterTagProps[] => {
  const selectedGroups = getSelectedGroups(classroom, studentIds);
  return selectedGroups.length
    ? selectedGroups.map(
        (group) =>
          ({
            filterKey: 'studentIds',
            value: group?.groupEnrollments?.map((g) => g?.student?.id),
            label: group.name,
            icon: faUsers,
          } as ReportFilterTagProps)
      )
    : [];
};

const getStudentsFilterTags = (
  classroom: Classroom,
  studentIds: number[]
): ReportFilterTagProps[] => {
  const selectedStudents = getSelectedStudents(classroom, studentIds);

  return selectedStudents.map(
    (student) =>
      ({
        filterKey: 'studentIds',
        value: [student?.id],
        label: `${student?.firstName} ${student?.lastName}`,
        icon: faUser,
      } as ReportFilterTagProps)
  );
};

export default ReportFilterTags;
